import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';
import { VocabCombobox } from '@/components/library/VocabCombobox';

/**
 * Sprint A — Evaluation Metadata Card (flat list, designed so future Sprint B
 * fields like Estimated Time, Passing Score, plus future Certification
 * fields slot in without restructuring).
 *
 * Mounts: Objective, Instructions, Evaluation Type (combobox), Professional
 * Area (combobox), Level (combobox), Language (combobox), Category (text),
 * available_for_company_use, available_for_individual_purchase (future-flag).
 *
 * Saves via PUT /admin/questionnaires/{qid}.
 */
const FIELD_KEYS = [
  'objective', 'instructions',
  'evaluation_type', 'professional_area', 'level', 'language', 'category',
  'available_for_company_use', 'available_for_individual_purchase',
];

export function EvaluationMetadataCard({ questionnaire, onSaved }) {
  const { t } = useTranslation();
  const [vocab, setVocab] = useState(null);
  const [form, setForm] = useState(() => initial(questionnaire));
  const [saving, setSaving] = useState(false);
  const dirty = isDirty(form, questionnaire);

  useEffect(() => {
    setForm(initial(questionnaire));
  }, [questionnaire?.id]);

  useEffect(() => {
    let alive = true;
    adminApi.getLibraryVocab()
      .then((v) => { if (alive) setVocab(v); })
      .catch((e) => toast.error(t('library.metadata.vocabLoadFailed', { defaultValue: 'Could not load vocab' }),
        { description: formatApiError(e) }));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setField = (key, val) => setForm((p) => ({ ...p, [key]: val }));

  const save = async () => {
    setSaving(true);
    try {
      const patch = {};
      FIELD_KEYS.forEach((k) => { patch[k] = form[k] ?? null; });
      const updated = await adminApi.updateQuestionnaireMeta(questionnaire.id, patch);
      toast.success(t('library.metadata.savedToast', { defaultValue: 'Metadata saved' }));
      onSaved?.(updated);
    } catch (e) {
      toast.error(t('toasts.saveFailed'), { description: formatApiError(e) });
    } finally {
      setSaving(false);
    }
  };

  const reloadVocab = (extendedEntry, field) => {
    if (!extendedEntry || !field) return;
    setVocab((prev) => {
      if (!prev) return prev;
      const list = prev[field] || [];
      // Replace by code if exists (re-archive flip), else append.
      const next = [...list.filter((v) => v.code !== extendedEntry.code), extendedEntry]
        .sort((a, b) => (a.label_en || '').localeCompare(b.label_en || ''));
      return { ...prev, [field]: next };
    });
  };

  return (
    <Card className="p-6 shadow-card space-y-5" data-testid="evaluation-metadata-card">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-bold tracking-tight">
            {t('library.metadata.title', { defaultValue: 'Catalog metadata' })}
          </h2>
          <p className="text-xs text-muted-foreground">
            {t('library.metadata.subtitle', { defaultValue: 'Properties that drive search, filters, and the assignment preview.' })}
          </p>
        </div>
        <Button
          size="sm"
          onClick={save}
          disabled={!dirty || saving}
          className="bg-gradient-primary gap-1.5"
          data-testid="evaluation-metadata-save"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          {t('common.save', { defaultValue: 'Save' })}
        </Button>
      </div>

      {/* Objective + Instructions */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="objective" className="text-xs">
            {t('library.metadata.objective.label', { defaultValue: 'Objective' })}
          </Label>
          <Textarea
            id="objective"
            data-testid="metadata-objective"
            rows={4}
            value={form.objective || ''}
            placeholder={t('library.metadata.objective.placeholder', { defaultValue: 'What does this evaluation aim to measure?' })}
            onChange={(e) => setField('objective', e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="instructions" className="text-xs">
            {t('library.metadata.instructions.label', { defaultValue: 'Instructions for participants' })}
          </Label>
          <Textarea
            id="instructions"
            data-testid="metadata-instructions"
            rows={4}
            value={form.instructions || ''}
            placeholder={t('library.metadata.instructions.placeholder', { defaultValue: 'How should the candidate approach this evaluation?' })}
            onChange={(e) => setField('instructions', e.target.value)}
          />
        </div>
      </div>

      {/* Comboboxes — DB-backed shared catalog (Sprint A.1.1). */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-xs">{t('library.metadata.evaluation_type.label', { defaultValue: 'Evaluation type' })}</Label>
          <VocabCombobox
            field="evaluation_type"
            testId="metadata-evaluation-type"
            options={vocab?.evaluation_type || []}
            value={form.evaluation_type}
            onChange={(v) => setField('evaluation_type', v)}
            onVocabExtended={(entry) => reloadVocab(entry, 'evaluation_type')}
            placeholder={t('library.metadata.evaluation_type.placeholder', { defaultValue: 'Search or create…' })}
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">{t('library.metadata.professional_area.label', { defaultValue: 'Professional area' })}</Label>
          <VocabCombobox
            field="professional_area"
            testId="metadata-professional-area"
            options={vocab?.professional_area || []}
            value={form.professional_area}
            onChange={(v) => setField('professional_area', v)}
            onVocabExtended={(entry) => reloadVocab(entry, 'professional_area')}
            placeholder={t('library.metadata.professional_area.placeholder', { defaultValue: 'Search or create…' })}
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">{t('library.metadata.level.label', { defaultValue: 'Level' })}</Label>
          <VocabCombobox
            field="level"
            testId="metadata-level"
            options={vocab?.level || []}
            value={form.level}
            onChange={(v) => setField('level', v)}
            onVocabExtended={(entry) => reloadVocab(entry, 'level')}
            placeholder={t('library.metadata.level.placeholder', { defaultValue: 'Search or create…' })}
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">{t('library.metadata.language.label', { defaultValue: 'Language' })}</Label>
          <VocabCombobox
            field="language"
            testId="metadata-language"
            options={vocab?.language || []}
            value={form.language}
            onChange={(v) => setField('language', v)}
            onVocabExtended={(entry) => reloadVocab(entry, 'language')}
            placeholder={t('library.metadata.language.placeholder', { defaultValue: 'Search or create…' })}
          />
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label className="text-xs">{t('library.metadata.category.label', { defaultValue: 'Category' })}</Label>
          <VocabCombobox
            field="category"
            testId="metadata-category"
            options={vocab?.category || []}
            value={form.category}
            onChange={(v) => setField('category', v)}
            onVocabExtended={(entry) => reloadVocab(entry, 'category')}
            placeholder={t('library.metadata.category.placeholder', { defaultValue: 'Search or create…' })}
          />
        </div>
      </div>

      {/* Availability toggles (kept as a flat list per founder spec). */}
      <div className="grid sm:grid-cols-2 gap-4 pt-1">
        <ToggleRow
          label={t('library.metadata.availability.company', { defaultValue: 'Available for company use' })}
          hint={t('library.metadata.availability.companyHint', { defaultValue: 'Visible inside the enterprise portal & CTH assignments.' })}
          checked={!!form.available_for_company_use}
          onCheckedChange={(v) => setField('available_for_company_use', v)}
          testId="metadata-avail-company"
        />
        <ToggleRow
          label={t('library.metadata.availability.individual', { defaultValue: 'Available for individual purchase' })}
          hint={t('library.metadata.availability.individualHint', { defaultValue: 'Reserved for the future marketplace. Safe to flip today.' })}
          futureBadge
          checked={!!form.available_for_individual_purchase}
          onCheckedChange={(v) => setField('available_for_individual_purchase', v)}
          testId="metadata-avail-individual"
        />
      </div>
    </Card>
  );
}

function ToggleRow({ label, hint, checked, onCheckedChange, testId, futureBadge }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-xl border border-border/70 p-3 flex items-start justify-between gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium">{label}</p>
          {futureBadge && (
            <span className="text-xs uppercase tracking-wider px-2 py-0.5 rounded bg-muted text-muted-foreground">
              {t('library.metadata.availability.futureBadge', { defaultValue: 'Future' })}
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onCheckedChange} data-testid={testId} />
    </div>
  );
}

function initial(q) {
  if (!q) return {};
  const out = {};
  FIELD_KEYS.forEach((k) => { out[k] = q[k] ?? (k.startsWith('available_for') ? !!q[k] : null); });
  return out;
}

function isDirty(form, q) {
  if (!q) return false;
  return FIELD_KEYS.some((k) => {
    const a = form[k] ?? null;
    const b = q[k] ?? null;
    if (k.startsWith('available_for')) return Boolean(a) !== Boolean(b);
    return (a || '') !== (b || '');
  });
}

export default EvaluationMetadataCard;
